import { clients } from '../lib/https';
import { useModelStore } from '../state/store/chat/use-model-store';
// import { Atom } from 'lucide-react';
import { ModelTypes, type IModel } from 'features/chat/types/model.types';
import type { IGetAgentModelResponse } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { ConfigurationSettings } from '/models/configuration-settings';
import modelIcon from '../assets/images/modelIcon.svg';
import {
  LlmModelProviderLabel,
  LlmModelRegion,
} from '../constant/llm-model';

export interface ILlmModelGetDTO {
  itemId: string;
  modelName: string;
  modelDisplayName: string;
  modelDescription: string;
  modelType: string;
  isActive: boolean;
  isThinkingModel: boolean;
  isDefaultModel: boolean;
  order: number;
  hasDynamicThinking?: boolean;
  isHarmonicModel?: boolean;
  region: LlmModelRegion;
  providerLabel: LlmModelProviderLabel;
  disallowed?: boolean;
  disallowedReason?: string;
  /** Plan rules would refuse or pause it, but the organization's top-up credits let it run. */
  viaTopUp?: boolean;
}

type IAgentModel = IGetAgentModelResponse[number];

const isPickableCustomModel = (model: IAgentModel) =>
  model.is_system === false &&
  !!model.id &&
  model.model_type === 'chat' &&
  model.is_active !== false &&
  model.status === 'validated';

const toCustomModel = (model: IAgentModel): IModel => ({
  id: model.id ?? '',
  name: model.model_name_label || model.model_name,
  description: '',
  modelType: ModelTypes.VISION,
  isThinkingModel: false,
  icon: <img src={modelIcon} alt={model.model_name} className="w-4 h-4" />,
  isDefaultModel: false,
  order: Number.MAX_SAFE_INTEGER,
  providerLabel: model.provider_label as LlmModelProviderLabel,
  paused: !!model.disallowed,
  pausedReason: model.disallowed_reason ?? '',
  isCustom: true,
});

export const getLlmModels = async (config: ConfigurationSettings): Promise<ILlmModelGetDTO[]> => {
  // Model catalogue is served by taskforce (file-backed, operator-managed on deploy)
  // instead of the legacy seliseai ModelConfiguration service.
  const url = `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.models}`;
  const agentModelsUrl = `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsModels}`;
  const agentModelsRequest = clients
    .get<IGetAgentModelResponse>(agentModelsUrl, config.blocksKey)
    .catch(() => undefined);
  const models = await clients.get<ILlmModelGetDTO[]>(url, config.blocksKey);
  if (models) {
    const platformModels = models
      .filter((model) => model.isActive)
      .sort((a, b) => a.order - b.order)
      .map((model) => {
        const icon = <img src={modelIcon} alt={model.modelDisplayName} className="w-4 h-4" />;

        const isDeepSeek =
          model.modelName?.toLowerCase().includes('deepseek') ||
          model.modelDisplayName?.toLowerCase().includes('deepseek');
        const modelType =
          isDeepSeek || model.modelType !== 'NonVision'
            ? ModelTypes.VISION
            : ModelTypes.NO_VISION;

        return {
          id: model.modelName,
          name: model.modelDisplayName,
          description: model.modelDescription,
          modelType,
          isThinkingModel: model.isThinkingModel,
          icon: icon,
          isDefaultModel: model.isDefaultModel,
          order: model.order,
          hasDynamicThinking: model.hasDynamicThinking ?? false,
          isHarmonicModel: model.isHarmonicModel ?? false,
          region: model.region,
          providerLabel: model.providerLabel,
          paused: !!model.disallowed && !model.viaTopUp,
          pausedReason: model.disallowedReason ?? '',
          viaTopUp: !!model.viaTopUp,
        };
      });
    const { setModels, models: storedModels } = useModelStore.getState();
    setModels([...platformModels, ...storedModels.filter((model) => model.isCustom)]);
    agentModelsRequest.then((agentModels) => {
      if (!agentModels) return;
      const customModels = agentModels.filter(isPickableCustomModel).map(toCustomModel);
      setModels([...platformModels, ...customModels]);
    });
  }
  return models;
};
